import { projectsProjectAccessDecisionSchema, } from '@crest/module-projects/contracts/project-access-decision';
import { projectsProjectReferenceSnapshotSchema, } from '@crest/module-projects/contracts/project-reference';
import { z } from 'zod';
import { insuranceClaimsCommandReceiptSchema, insuranceClaimsIdSchema, insuranceClaimsMoneyCentsSchema, insuranceClaimsPageSchema, } from './insurance-claims-claim.contract.js';
export const insuranceClaimsScopesCapability = 'insurance-claims.scopes.v1';
export const insuranceClaimsScopeVersionsCapability = 'insurance-claims.scope-versions.v1';
export const insuranceClaimsEstimateSplitAuditsCapability = 'insurance-claims.estimate-split-audits.v1';
export const insuranceClaimsScopeTypeSchema = z.enum([
    'mitigation', 'reconstruction', 'mold', 'contents', 'cleaning', 'other',
]);
export const insuranceClaimsScopeStatusSchema = z.enum([
    'active', 'completed', 'on_hold', 'disputed', 'cancelled',
]);
export const insuranceClaimsScopeWorkflowStageSchema = z.enum([
    'draft', 'review_ready', 'confirmed', 'bypassed',
]);
export const insuranceClaimsScopeSourceTypeSchema = z.enum([
    'typed', 'video', 'intake', 'manual', 'assistant', 'import',
]);
export const insuranceClaimsScopeCustomerModeSchema = z.enum([
    'standard', 'customer_limited',
]);
export const insuranceClaimsScopeCoveragePositionSchema = z.enum([
    'unknown', 'covered', 'partially_covered', 'not_covered', 'disputed',
]);
export const insuranceClaimsEstimateVerificationSchema = z.enum([
    'operator-attested', 'provider-verified',
]);
export const insuranceClaimsEstimateSourceUnavailableReasonSchema = z.enum([
    'not-installed',
    'not-enabled',
    'permission-denied',
    'no-current-estimate',
    'preferred-estimate-not-found',
    'provider-failed',
    'provider-invalid',
]);
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const insuranceClaimsEstimateReferenceSchema = z.strictObject({
    capability: z.literal('estimating.estimates.v1'),
    estimateId: z.string().uuid(),
    estimateVersionId: z.string().uuid(),
    contentHash: sha256Schema,
    currencyCode: z.string().regex(/^[A-Z]{3}$/),
    totalCents: insuranceClaimsMoneyCentsSchema,
    verification: insuranceClaimsEstimateVerificationSchema,
    attestationNote: nullableText(2_000).default(null),
}).superRefine((value, context) => {
    if (value.verification === 'operator-attested' &&
        (value.attestationNote === null || value.attestationNote.length < 10)) {
        context.addIssue({
            code: 'custom',
            path: ['attestationNote'],
            message: 'Operator-attested estimate references require a retained explanation.',
        });
    }
});
export const insuranceClaimsEstimateAllocationSchema = z.strictObject({
    source: insuranceClaimsEstimateReferenceSchema,
    allocatedAmountCents: insuranceClaimsMoneyCentsSchema,
});
const insuranceClaimsScopeDraftObjectSchema = z.strictObject({
    claimId: insuranceClaimsIdSchema,
    name: z.string().trim().min(1).max(200),
    scopeType: insuranceClaimsScopeTypeSchema,
    description: nullableText(8_000).default(null),
    sourceType: insuranceClaimsScopeSourceTypeSchema,
    customerScopeMode: insuranceClaimsScopeCustomerModeSchema,
    customerLimitations: nullableText(4_000).default(null),
    coveragePosition: insuranceClaimsScopeCoveragePositionSchema,
    coverageNotes: nullableText(4_000).default(null),
    estimateAllocation: insuranceClaimsEstimateAllocationSchema.nullable().default(null),
    sortOrder: z.number().int().min(0).max(10_000),
});
export const insuranceClaimsScopeDraftSchema = insuranceClaimsScopeDraftObjectSchema
    .superRefine((value, context) => {
    if (value.customerScopeMode === 'customer_limited' && value.customerLimitations === null) {
        context.addIssue({
            code: 'custom',
            path: ['customerLimitations'],
            message: 'Customer-limited scopes require retained limitations.',
        });
    }
    if (value.estimateAllocation !== null &&
        value.estimateAllocation.allocatedAmountCents > value.estimateAllocation.source.totalCents) {
        context.addIssue({
            code: 'custom',
            path: ['estimateAllocation', 'allocatedAmountCents'],
            message: 'One scope allocation cannot exceed the retained estimate total.',
        });
    }
});
export const insuranceClaimsCreateScopeCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: z.string().uuid(),
    scopeId: insuranceClaimsIdSchema,
    scope: insuranceClaimsScopeDraftSchema,
    reason: z.string().trim().min(10).max(500),
});
const insuranceClaimsScopeCommandBase = {
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
};
export const insuranceClaimsReviseScopeCommandSchema = z.strictObject({
    kind: z.literal('revise'),
    ...insuranceClaimsScopeCommandBase,
    scope: insuranceClaimsScopeDraftSchema,
});
export const insuranceClaimsScopeWorkflowCommandSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('mark-review-ready'),
        ...insuranceClaimsScopeCommandBase,
        decisionNote: nullableText(2_000).default(null),
    }),
    z.strictObject({
        kind: z.literal('confirm'),
        ...insuranceClaimsScopeCommandBase,
        decisionNote: nullableText(2_000).default(null),
    }),
    z.strictObject({
        kind: z.literal('bypass'),
        ...insuranceClaimsScopeCommandBase,
        bypassNote: z.string().trim().min(10).max(2_000),
    }),
]);
export const insuranceClaimsScopeLifecycleCommandSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('transition'),
        ...insuranceClaimsScopeCommandBase,
        targetStatus: insuranceClaimsScopeStatusSchema,
    }),
    z.strictObject({ kind: z.literal('archive'), ...insuranceClaimsScopeCommandBase }),
    z.strictObject({ kind: z.literal('restore'), ...insuranceClaimsScopeCommandBase }),
]);
export const insuranceClaimsScopeCommandSchema = z.discriminatedUnion('kind', [
    insuranceClaimsReviseScopeCommandSchema,
    ...insuranceClaimsScopeWorkflowCommandSchema.options,
    ...insuranceClaimsScopeLifecycleCommandSchema.options,
]);
export const insuranceClaimsScopePublicDataSchema = z.strictObject({
    projectReference: projectsProjectReferenceSnapshotSchema,
    projectAccessDecision: projectsProjectAccessDecisionSchema,
    name: z.string().trim().min(1).max(200),
    scopeType: insuranceClaimsScopeTypeSchema,
    description: nullableText(8_000),
    sourceType: insuranceClaimsScopeSourceTypeSchema,
    customerScopeMode: insuranceClaimsScopeCustomerModeSchema,
    customerLimitations: nullableText(4_000),
    coveragePosition: insuranceClaimsScopeCoveragePositionSchema,
    coverageNotes: nullableText(4_000),
    estimateAllocation: insuranceClaimsEstimateAllocationSchema.nullable(),
    sortOrder: z.number().int().min(0).max(10_000),
});
export const insuranceClaimsScopeVersionSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsScopeVersionsCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    scopeId: insuranceClaimsIdSchema,
    claimId: insuranceClaimsIdSchema,
    projectId: z.string().uuid(),
    revision: z.number().int().positive(),
    status: insuranceClaimsScopeStatusSchema,
    workflowStage: insuranceClaimsScopeWorkflowStageSchema,
    decisionNote: nullableText(2_000),
    bypassNote: nullableText(2_000),
    contentHash: sha256Schema,
    reason: z.string().trim().min(10).max(500),
    actorMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    data: insuranceClaimsScopePublicDataSchema,
});
export const insuranceClaimsScopeSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsScopesCapability),
    tenantId: z.string().uuid(),
    id: insuranceClaimsIdSchema,
    claimId: insuranceClaimsIdSchema,
    projectId: z.string().uuid(),
    status: insuranceClaimsScopeStatusSchema,
    workflowStage: insuranceClaimsScopeWorkflowStageSchema,
    revision: z.number().int().positive(),
    currentVersionId: z.string().uuid(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    currentVersion: insuranceClaimsScopeVersionSchema,
});
export const insuranceClaimsScopeListQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    claimId: insuranceClaimsIdSchema.nullable().default(null),
    projectId: z.string().uuid().nullable().default(null),
    status: insuranceClaimsScopeStatusSchema.nullable().default(null),
    workflowStage: insuranceClaimsScopeWorkflowStageSchema.nullable().default(null),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const insuranceClaimsScopeResponseSchema = z.strictObject({
    data: insuranceClaimsScopeSchema,
    command: insuranceClaimsCommandReceiptSchema.optional(),
});
export const insuranceClaimsScopesResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsScopeSchema),
    page: insuranceClaimsPageSchema,
});
export const insuranceClaimsScopeVersionsResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsScopeVersionSchema),
});
export const insuranceClaimsEstimateSourceAvailabilitySchema = z.discriminatedUnion('state', [
    z.strictObject({
        state: z.literal('present'),
        source: insuranceClaimsEstimateReferenceSchema,
    }),
    z.strictObject({
        state: z.literal('unavailable'),
        capability: z.literal('estimating.estimates.v1'),
        reasonCode: insuranceClaimsEstimateSourceUnavailableReasonSchema,
        reason: z.string().trim().min(1).max(500),
    }),
]);
export const insuranceClaimsEstimateSplitAuditStatusSchema = z.enum([
    'provider-unavailable',
    'not-configured',
    'incomplete',
    'stale',
    'under-allocated',
    'over-allocated',
    'balanced',
]);
export const insuranceClaimsEstimateSplitAuditSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsEstimateSplitAuditsCapability),
    claimId: insuranceClaimsIdSchema,
    projectId: z.string().uuid(),
    status: insuranceClaimsEstimateSplitAuditStatusSchema,
    source: insuranceClaimsEstimateReferenceSchema.nullable(),
    sourceUnavailableReasonCode: insuranceClaimsEstimateSourceUnavailableReasonSchema.nullable(),
    allocations: z.array(z.strictObject({
        scopeId: insuranceClaimsIdSchema,
        scopeVersionId: z.string().uuid(),
        scopeName: z.string().trim().min(1).max(200),
        allocatedAmountCents: insuranceClaimsMoneyCentsSchema,
        verification: insuranceClaimsEstimateVerificationSchema,
    })),
    allocatedTotalCents: insuranceClaimsMoneyCentsSchema.nullable(),
    differenceCents: z.number().int().min(-999_999_999_999).max(999_999_999_999).nullable(),
    missingScopeIds: z.array(insuranceClaimsIdSchema),
    staleScopeIds: z.array(insuranceClaimsIdSchema),
});
export const insuranceClaimsEstimateSplitAuditResponseSchema = z.strictObject({
    data: insuranceClaimsEstimateSplitAuditSchema,
});
export function computeInsuranceClaimsEstimateSplitAudit(input) {
    const toleranceCents = z.number().int().min(0).max(100_000)
        .parse(input.toleranceCents ?? 0);
    const activeScopes = input.scopes.filter((scope) => scope.archivedAt === null && scope.status !== 'cancelled');
    const allocations = activeScopes.flatMap((scope) => {
        const allocation = scope.currentVersion.data.estimateAllocation;
        return allocation === null ? [] : [{
                scopeId: scope.id,
                scopeVersionId: scope.currentVersionId,
                scopeName: scope.currentVersion.data.name,
                allocatedAmountCents: allocation.allocatedAmountCents,
                verification: allocation.source.verification,
                source: allocation.source,
            }];
    });
    const missingScopeIds = activeScopes
        .filter((scope) => scope.currentVersion.data.estimateAllocation === null)
        .map((scope) => scope.id);
    if (input.source.state === 'unavailable') {
        return insuranceClaimsEstimateSplitAuditSchema.parse({
            contractVersion: insuranceClaimsEstimateSplitAuditsCapability,
            claimId: input.claimId,
            projectId: input.projectId,
            status: 'provider-unavailable',
            source: null,
            sourceUnavailableReasonCode: input.source.reasonCode,
            allocations: allocations.map(({ source: _source, ...allocation }) => allocation),
            allocatedTotalCents: null,
            differenceCents: null,
            missingScopeIds,
            staleScopeIds: [],
        });
    }
    const source = input.source.source;
    const staleScopeIds = allocations
        .filter((allocation) => allocation.source.estimateId !== source.estimateId ||
        allocation.source.estimateVersionId !== source.estimateVersionId ||
        allocation.source.contentHash !== source.contentHash ||
        allocation.source.currencyCode !== source.currencyCode ||
        allocation.source.totalCents !== source.totalCents)
        .map((allocation) => allocation.scopeId);
    const allocatedTotalCents = allocations.reduce((sum, allocation) => sum + allocation.allocatedAmountCents, 0);
    const differenceCents = source.totalCents - allocatedTotalCents;
    const status = allocations.length === 0
        ? 'not-configured'
        : staleScopeIds.length > 0
            ? 'stale'
            : missingScopeIds.length > 0
                ? 'incomplete'
                : Math.abs(differenceCents) <= toleranceCents
                    ? 'balanced'
                    : differenceCents > 0
                        ? 'under-allocated'
                        : 'over-allocated';
    return insuranceClaimsEstimateSplitAuditSchema.parse({
        contractVersion: insuranceClaimsEstimateSplitAuditsCapability,
        claimId: input.claimId,
        projectId: input.projectId,
        status,
        source,
        sourceUnavailableReasonCode: null,
        allocations: allocations.map(({ source: _source, ...allocation }) => allocation),
        allocatedTotalCents,
        differenceCents,
        missingScopeIds,
        staleScopeIds,
    });
}
//# sourceMappingURL=insurance-claims-scope.contract.js.map